import { useState, type ReactNode } from 'react';
import { Popover, PopoverTrigger, PopoverContent, Button, Input } from '@/components/ui';
import { useSwitchPluginRef } from '../../hooks/usePlugins';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../services/api';
import type { Plugin } from '../../types';

interface SwitchRefPopoverProps {
  plugin: Plugin;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export default function SwitchRefPopover({
  plugin,
  open,
  onOpenChange,
  children,
}: SwitchRefPopoverProps) {
  const { success } = useToast();
  const switchRef = useSwitchPluginRef();
  const [ref, setRef] = useState(plugin.source.ref);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    if (!ref.trim() || ref.trim() === plugin.source.ref) {
      onOpenChange(false);
      return;
    }
    try {
      await switchRef.mutateAsync({ pluginId: plugin.pluginId, ref: ref.trim() });
      success(`Switched to ${ref.trim()}`);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'REF_NOT_FOUND') {
        setError("That ref doesn't exist on the remote.");
      } else {
        setError(err instanceof Error ? err.message : 'Failed to switch ref');
      }
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setRef(plugin.source.ref);
          setError(null);
        }
        onOpenChange(next);
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground" htmlFor="switch-ref">
              Switch ref
            </label>
            <Input
              id="switch-ref"
              value={ref}
              onChange={(e: { target: { value: string } }) => setRef(e.target.value)}
              placeholder="Branch, tag, or commit SHA"
              className="mt-1"
            />
            {error && <p className="mt-1 text-xs text-status-error-fg">{error}</p>}
          </div>
          <p className="text-xs text-muted-foreground">
            Switching does not apply changes. Click Sync to pull this ref's content.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} isLoading={switchRef.isPending}>
              Switch
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
